import { useState } from "react";
import { useNavigate } from "react-router";
import { useAppSelector, useAppDispatch } from "../../state/store";
import { SettingsProps } from "./";
import { Selection } from "../Inputs";
import { Check } from "../Check/Check";
import { Row } from "../Row";
import { SET_SETTINGS } from "../../state/reducers/settings";
import { QMSpeeds, SettingsData } from "../../types/settings";
import { ReactComponent as DarkMode } from "../../icons/dark-mode-icon.svg";
import { ReactComponent as LightMode } from "../../icons/light-mode-icon.svg";
import "./settings.scss";

export function SettingsSelector(props: SettingsProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const settings = useAppSelector((state) => state.settings);
  const [editedSettings, setEditedSettings] = useState<SettingsData>(settings);

  const speedOptions: { title: string; value: QMSpeeds }[] = [
    { title: "Slow", value: "SLOW" },
    { title: "Medium", value: "MEDIUM" },
    { title: "Fast", value: "FAST" },
  ];
  return (
    <div className="settings-container">
      <div className="settings-top-sticky">
        <button
          className="clickable"
          style={{ marginLeft: 10, marginBottom: 10 }}
          onClick={() => {
            navigate(-1);
            dispatch({ type: SET_SETTINGS, payload: editedSettings });
            props.onClickBack?.(editedSettings);
          }}
        >
          Back
        </button>
      </div>
      <div className="settings-scrolling">
        <h2>General Settings</h2>
        <Check
          checked={editedSettings.touchscreen}
          onClick={(checked) =>
            setEditedSettings({ ...editedSettings, touchscreen: checked })
          }
        >
          Touchscreen-Friendly Interface
        </Check>
        <p style={{ fontSize: 12, marginTop: -2 }}>
          Check if you have a touchscreen rather than a keyboard. If you have
          both, take your pick.
        </p>
        <Row>
          <Check
            checked={editedSettings.lightMode}
            onClick={() =>
              setEditedSettings({ ...editedSettings, lightMode: true })
            }
            style={{ width: 150 }}
          >
            <LightMode width={16} height={16} style={{ marginRight: 4 }} />
            Light Mode
          </Check>
          <Check
            checked={!editedSettings.lightMode}
            onClick={() =>
              setEditedSettings({ ...editedSettings, lightMode: false })
            }
            style={{ width: 150 }}
          >
            <DarkMode width={16} height={16} style={{ marginRight: 4 }} />
            Dark Mode
          </Check>
        </Row>

        <hr className="separator-line" />
        <h2>{"Quiz Now & Multiplayer Practice"}</h2>
        <Check
          checked={editedSettings.qnBookName}
          onClick={(e) =>
            setEditedSettings({ ...editedSettings, qnBookName: e })
          }
        >
          Display Book Name
        </Check>
        <p style={{ marginBottom: 0 }}>Quizmaster Reading Speed</p>
        <Row style={{ marginBottom: 10 }}>
          {speedOptions.map((opt) => (
            <Check
              key={opt.value}
              style={{ width: 105 }}
              checked={editedSettings.qmSpeed === opt.value}
              onClick={() =>
                setEditedSettings({ ...editedSettings, qmSpeed: opt.value })
              }
            >
              {opt.title}
            </Check>
          ))}
        </Row>
        <Check
          checked={editedSettings.qmPrecise}
          onClick={(e) =>
            setEditedSettings({ ...editedSettings, qmPrecise: e })
          }
        >
          Precise Quizmaster Reading Speed
        </Check>
        <p style={{ fontSize: 12, marginTop: -2 }}>
          Adjusts the speed of the quizmaster based on the number of syllables
          in each word
        </p>
        <hr className="separator-line" />
        <h2>Verse Cards & Question Drill</h2>
        <Selection
          caption="Verse Order"
          value={editedSettings.vcRandomize}
          values={{
            IN_ORDER: "In Order",
            RANDOM: "Randomized",
            REVERSE: "Reverse Order",
          }}
          onChange={(vcRandomize) =>
            setEditedSettings({ ...editedSettings, vcRandomize })
          }
        />
        <Check
          checked={editedSettings.vcReview}
          style={{ marginTop: 5 }}
          onClick={(vcReview) =>
            setEditedSettings({ ...editedSettings, vcReview })
          }
        >
          Review Missed Verses
        </Check>
        <p style={{ fontSize: 12, marginTop: -2 }}>
          Moves all missed verses to the bottom of the deck. If you throw out a
          verse, it will get moved to the bottom no matter what.
        </p>
        <Check
          checked={editedSettings.vcDisableAnimation}
          onClick={(vcDisableAnimation) =>
            setEditedSettings({ ...editedSettings, vcDisableAnimation })
          }
        >
          Disable Animations in Verse Cards
        </Check>
        <Check
          checked={editedSettings.vcVoiceToText}
          onClick={(vcVoiceToText) =>
            setEditedSettings({ ...editedSettings, vcVoiceToText })
          }
        >
          Use Voice to Text
        </Check>
        <p style={{ fontSize: 12, marginTop: -2 }}>
          Voice to Text in Verse Cards works best on Chrome on a desktop or
          laptop computer. May also work on Edge, Android, or other browsers or
          devices, but not on iOS. Please ensure your browser has microphone
          access.
        </p>
        <hr className="separator-line" />
        <h2>Memorize</h2>
        <Selection
          caption="Review Verses"
          value={String(editedSettings.memReview)}
          onChange={(value) =>
            setEditedSettings({
              ...editedSettings,
              memReview: Number.parseInt(value),
            })
          }
          values={{
            "0": "Never",
            "3": "Every 3 Verses",
            "5": "Every 5 Verses",
          }}
          belowText="How often to review verses that you previously memorized"
        />
        <Selection
          caption="Reference Input"
          value={editedSettings.memRefInput}
          onChange={(memRefInput) =>
            setEditedSettings({ ...editedSettings, memRefInput })
          }
          values={{
            NONE: "None",
            FULL: "Full Reference",
            CHAPTER: "Chapter and Verse",
            VERSE: "Verse Number Only",
          }}
          belowText="Input the reference on the first and last iterations to help you attach it to the verse"
        />
        <hr className="separator-line" />
        <h2>Scorekeeper</h2>
        <p>Go into a season in Scorekeeper to edit settings there.</p>
      </div>
    </div>
  );
}
